/**
 * ThresholdOptimizer: High-Precision Automatic Threshold & Edge Barrier Engine
 * 
 * Automatically calculates the optimal color similarity threshold for backdrop removal
 * and prevents flood fills from leaking into foreground subjects (even when colors are similar,
 * such as white shoe laces or clothing on a white studio background).
 */

export interface ThresholdOptimizationResult {
  optimalTolerance: number;
  bgR: number;
  bgG: number;
  bgB: number;
  bgA: number;
  noiseFloor: number;
  boundaryContrast: number;
}

export interface ComponentInfo {
  label: number;
  pixelCount: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  touchesBorder: boolean;
  isCorner: boolean;
  centroidX: number;
  centroidY: number;
}

export interface EdgePurgeResult {
  purgedPixels: number;
  purgedComponents: number;
  boundingBox: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null;
}

export interface SmartMaskResult {
  mask: Uint8Array;
  usedTolerance: number;
  isAuto: boolean;
  boundingBox: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null;
  selectedCount: number;
  purgedCornerPixels: number;
}

export class ThresholdOptimizer {
  /**
   * Analyzes local background texture and radial edge barriers to calculate the optimal threshold.
   */
  public static optimizeThreshold(
    imgData: ImageData,
    seedX: number,
    seedY: number,
    width: number,
    height: number
  ): ThresholdOptimizationResult {
    const data = imgData.data;
    seedX = Math.max(0, Math.min(width - 1, Math.floor(seedX)));
    seedY = Math.max(0, Math.min(height - 1, Math.floor(seedY)));

    const centerIdx = (seedY * width + seedX) * 4;
    const startA = data[centerIdx + 3];

    // 1. Sample 9x9 local window around seed to determine stable background baseline & noise
    let sumR = 0, sumG = 0, sumB = 0;
    const patchRadius = 4;
    const samples: { r: number; g: number; b: number }[] = [];

    for (let dy = -patchRadius; dy <= patchRadius; dy++) {
      const py = seedY + dy;
      if (py < 0 || py >= height) continue;
      for (let dx = -patchRadius; dx <= patchRadius; dx++) {
        const px = seedX + dx;
        if (px < 0 || px >= width) continue;
        const idx = (py * width + px) * 4;
        const a = data[idx + 3];
        if (startA === 0 && a === 0) continue;
        if (a < 15) continue;

        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        sumR += r;
        sumG += g;
        sumB += b;
        samples.push({ r, g, b });
      }
    }

    const bgR = samples.length > 0 ? Math.round(sumR / samples.length) : data[centerIdx];
    const bgG = samples.length > 0 ? Math.round(sumG / samples.length) : data[centerIdx + 1];
    const bgB = samples.length > 0 ? Math.round(sumB / samples.length) : data[centerIdx + 2];

    // Measure maximum local noise floor (compression noise, lighting texture)
    let maxNoise = 3;
    for (const p of samples) {
      const dev = Math.max(Math.abs(p.r - bgR), Math.abs(p.g - bgG), Math.abs(p.b - bgB));
      if (dev > maxNoise) maxNoise = dev;
    }
    const noiseFloor = Math.max(6, Math.min(24, Math.ceil(maxNoise * 1.5)));

    // 2. Cast 16 radial rays outward from seed to find the nearest foreground contrast boundary
    const numRays = 16;
    const maxRadius = Math.min(width, height, 400);
    const boundaryDistances: number[] = [];

    for (let i = 0; i < numRays; i++) {
      const angle = (i * 2 * Math.PI) / numRays;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);

      let prevDist = 0;
      for (let r = 2; r < maxRadius; r += 2) {
        const qx = Math.round(seedX + r * cosA);
        const qy = Math.round(seedY + r * sinA);
        if (qx < 0 || qx >= width || qy < 0 || qy >= height) break;

        const idx = (qy * width + qx) * 4;
        if (data[idx + 3] < 10) continue;

        const pr = data[idx];
        const pg = data[idx + 1];
        const pb = data[idx + 2];
        const dist = Math.max(Math.abs(pr - bgR), Math.abs(pg - bgG), Math.abs(pb - bgB));

        // Detect sharp gradient jump into foreground
        const step = dist - prevDist;
        if (step >= 14 && dist > noiseFloor + 4) {
          boundaryDistances.push(prevDist);
          break;
        }
        prevDist = dist;
      }
    }

    let optimalTolerance: number;
    let boundaryContrast = 32;

    if (boundaryDistances.length >= 3) {
      boundaryDistances.sort((a, b) => a - b);
      // Use 25th percentile of boundary hits to be safely conservative
      const qIdx = Math.floor(boundaryDistances.length * 0.25);
      boundaryContrast = boundaryDistances[qIdx];
      // Keep tolerance safely under the foreground boundary
      optimalTolerance = Math.max(noiseFloor + 4, Math.floor(boundaryContrast * 0.82));
    } else {
      // If flat studio backdrop without near boundaries, use noise floor + safe headroom
      optimalTolerance = Math.max(noiseFloor + 8, 28);
    }

    // Operating clamp: ensure tolerance stays within solid bounds [12, 55]
    optimalTolerance = Math.max(12, Math.min(55, optimalTolerance));

    return {
      optimalTolerance,
      bgR,
      bgG,
      bgB,
      bgA: startA,
      noiseFloor,
      boundaryContrast
    };
  }

  /**
   * Smart Edge-Preserving Flood Fill:
   * Combines global color distance matching with local gradient step barrier protection.
   * Will NEVER leak across sharp contrast edges into foreground objects (like white laces or light clothes).
   */
  public static smartFlood(
    sourceCtx: CanvasRenderingContext2D,
    width: number,
    height: number,
    seeds: { x: number; y: number }[],
    options: {
      autoTolerance?: boolean;
      manualTolerance?: number;
      contiguous?: boolean;
      featherEdge?: boolean;
    } = {}
  ): SmartMaskResult {
    const imgData = sourceCtx.getImageData(0, 0, width, height);
    const data = imgData.data;

    const isAuto = options.autoTolerance !== false;
    let tolerance = options.manualTolerance ?? 32;
    const contiguous = options.contiguous !== false;

    // Detect background palette from primary seeds
    let bgR = 255, bgG = 255, bgB = 255, bgA = 255;
    if (seeds.length > 0) {
      const primary = seeds[0];
      const opt = this.optimizeThreshold(imgData, primary.x, primary.y, width, height);
      bgR = opt.bgR;
      bgG = opt.bgG;
      bgB = opt.bgB;
      bgA = opt.bgA;
      if (isAuto) {
        tolerance = opt.optimalTolerance;
      }
    }

    // Step limit barrier: prevents flood from jumping across sudden gradient steps
    // e.g. If tolerance is 30, stepLimit is ~20. A contrast edge jump will not be crossed.
    const stepLimit = Math.max(12, Math.floor(tolerance * 0.70));

    const mask = new Uint8Array(width * height);
    const visited = new Uint8Array(width * height);
    const queue = new Int32Array(width * height * 2);
    let head = 0;
    let tail = 0;
    let selectedCount = 0;

    // Fast color match check
    const matchesSeed = (r: number, g: number, b: number, a: number): boolean => {
      if (bgA === 0 && a === 0) return true;
      if (a < 10) return true;
      if (Math.abs(a - bgA) > tolerance) return false;
      return (
        Math.abs(r - bgR) <= tolerance &&
        Math.abs(g - bgG) <= tolerance &&
        Math.abs(b - bgB) <= tolerance
      );
    };

    // Fast step difference check
    const matchesStep = (r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): boolean => {
      return (
        Math.abs(r1 - r2) <= stepLimit &&
        Math.abs(g1 - g2) <= stepLimit &&
        Math.abs(b1 - b2) <= stepLimit
      );
    };

    if (!contiguous) {
      // Global selection with threshold
      for (let y = 0; y < height; y++) {
        const row = y * width;
        for (let x = 0; x < width; x++) {
          const idx = (row + x) * 4;
          if (matchesSeed(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])) {
            mask[row + x] = 255;
            selectedCount++;
          }
        }
      }
    } else {
      // Contiguous BFS with dual-condition barrier protection
      for (const seed of seeds) {
        const sx = Math.max(0, Math.min(width - 1, Math.floor(seed.x)));
        const sy = Math.max(0, Math.min(height - 1, Math.floor(seed.y)));
        const sIdx = sy * width + sx;
        if (visited[sIdx] === 0) {
          visited[sIdx] = 1;
          queue[tail++] = sx;
          queue[tail++] = sy;
        }
      }

      while (head < tail) {
        const cx = queue[head++];
        const cy = queue[head++];
        const cIdx = cy * width + cx;
        const cdIdx = cIdx * 4;

        mask[cIdx] = 255;
        selectedCount++;

        const curR = data[cdIdx];
        const curG = data[cdIdx + 1];
        const curB = data[cdIdx + 2];

        // 4-way neighbors
        const neighbors = [
          cx + 1, cy,
          cx - 1, cy,
          cx, cy + 1,
          cx, cy - 1
        ];

        for (let i = 0; i < 8; i += 2) {
          const nx = neighbors[i];
          const ny = neighbors[i + 1];

          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            const nIdx = ny * width + nx;
            if (visited[nIdx] === 0) {
              const ndIdx = nIdx * 4;
              const nA = data[ndIdx + 3];

              if (nA < 10) {
                // Transparent pixel is naturally part of background
                visited[nIdx] = 1;
                queue[tail++] = nx;
                queue[tail++] = ny;
              } else {
                const nR = data[ndIdx];
                const nG = data[ndIdx + 1];
                const nB = data[ndIdx + 2];

                // Gate: must match background baseline AND not cross sudden step barrier
                if (matchesSeed(nR, nG, nB, nA) && matchesStep(curR, curG, curB, nR, nG, nB)) {
                  visited[nIdx] = 1;
                  queue[tail++] = nx;
                  queue[tail++] = ny;
                }
              }
            }
          }
        }
      }
    }

    // Purge disconnected corner/edge vignette artifacts into the background mask
    const purgeRes = this.purgeEdgeArtifacts(data, width, height, mask, 15);
    selectedCount += purgeRes.purgedPixels;

    // Foreground bounding box strictly from remaining unmasked main subject
    const boundingBox = purgeRes.boundingBox;

    return {
      mask,
      usedTolerance: tolerance,
      isAuto,
      boundingBox,
      selectedCount,
      purgedCornerPixels: purgeRes.purgedPixels
    };
  }

  /**
   * Identifies all connected opaque foreground components (alpha > minAlpha).
   * Internal holes and transparencies (like a basket with weave holes or shoe air bubbles)
   * are preserved because they have alpha <= minAlpha.
   */
  public static findConnectedComponents(
    data: Uint8ClampedArray | Uint8Array,
    width: number,
    height: number,
    minAlpha: number = 15,
    maskFilter?: Uint8Array
  ): { labels: Int32Array; components: ComponentInfo[] } {
    const labels = new Int32Array(width * height);
    let nextLabel = 1;
    const components: ComponentInfo[] = [];
    const queue = new Int32Array(width * height * 2);

    const cornerRadiusX = Math.max(12, Math.floor(width * 0.15));
    const cornerRadiusY = Math.max(12, Math.floor(height * 0.15));

    for (let y = 0; y < height; y++) {
      const row = y * width;
      for (let x = 0; x < width; x++) {
        const idx = row + x;
        if (maskFilter && maskFilter[idx] !== 0) continue;
        const alpha = data[idx * 4 + 3];
        if (alpha <= minAlpha || labels[idx] > 0) continue;

        const label = nextLabel++;
        let pixelCount = 0;
        let minX = x, maxX = x, minY = y, maxY = y;
        let touchesBorder = false;
        let isCorner = false;
        let sumX = 0, sumY = 0;

        let qHead = 0, qTail = 0;
        queue[qTail++] = x;
        queue[qTail++] = y;
        labels[idx] = label;

        while (qHead < qTail) {
          const cx = queue[qHead++];
          const cy = queue[qHead++];
          pixelCount++;
          sumX += cx;
          sumY += cy;

          if (cx < minX) minX = cx;
          if (cx > maxX) maxX = cx;
          if (cy < minY) minY = cy;
          if (cy > maxY) maxY = cy;

          if (cx === 0 || cx === width - 1 || cy === 0 || cy === height - 1) {
            touchesBorder = true;
          }

          if (
            (cx <= cornerRadiusX && cy <= cornerRadiusY) ||
            (cx >= width - 1 - cornerRadiusX && cy <= cornerRadiusY) ||
            (cx <= cornerRadiusX && cy >= height - 1 - cornerRadiusY) ||
            (cx >= width - 1 - cornerRadiusX && cy >= height - 1 - cornerRadiusY)
          ) {
            isCorner = true;
          }

          const nbrs = [cx + 1, cy, cx - 1, cy, cx, cy + 1, cx, cy - 1];
          for (let i = 0; i < 8; i += 2) {
            const nx = nbrs[i];
            const ny = nbrs[i + 1];
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const nIdx = ny * width + nx;
              if (maskFilter && maskFilter[nIdx] !== 0) continue;
              if (labels[nIdx] === 0 && data[nIdx * 4 + 3] > minAlpha) {
                labels[nIdx] = label;
                queue[qTail++] = nx;
                queue[qTail++] = ny;
              }
            }
          }
        }

        components.push({
          label,
          pixelCount,
          minX,
          maxX,
          minY,
          maxY,
          touchesBorder,
          isCorner,
          centroidX: Math.round(sumX / pixelCount),
          centroidY: Math.round(sumY / pixelCount)
        });
      }
    }

    return { labels, components };
  }

  /**
   * Purges edge, corner, and vignette noise islands that are disconnected from the main object.
   * If a mask is provided, the purged pixels are marked in the mask as background (mask[i] = 255).
   * In the image data, their alpha is set to 0.
   */
  public static purgeEdgeArtifacts(
    data: Uint8ClampedArray | Uint8Array,
    width: number,
    height: number,
    mask?: Uint8Array,
    minAlpha: number = 15
  ): EdgePurgeResult {
    const { labels, components } = this.findConnectedComponents(data, width, height, minAlpha, mask);

    if (components.length === 0) {
      return { purgedPixels: 0, purgedComponents: 0, boundingBox: null };
    }

    // 1. Identify primary foreground subject (largest component)
    let mainComponent = components[0];
    for (const c of components) {
      if (c.pixelCount > mainComponent.pixelCount) {
        mainComponent = c;
      }
    }

    // 2. Filter out disconnected edge/corner artifacts
    let purgedPixels = 0;
    let purgedComponents = 0;

    for (const c of components) {
      if (c.label === mainComponent.label) continue;

      // Keep substantial multi-part pieces (e.g. 2nd shoe or disconnected primary subject > 18% of main)
      if (c.pixelCount >= mainComponent.pixelCount * 0.18) {
        continue;
      }

      // Purge if touches border or corner and area < 18% of main subject, OR if it's small isolated dust (< 800 px)
      const isEdgeOrCornerNoise = (c.touchesBorder || c.isCorner) && c.pixelCount < mainComponent.pixelCount * 0.18;
      const isDust = c.pixelCount < Math.min(800, Math.floor(mainComponent.pixelCount * 0.03));

      if (isEdgeOrCornerNoise || isDust) {
        purgedComponents++;
        for (let y = c.minY; y <= c.maxY; y++) {
          const row = y * width;
          for (let x = c.minX; x <= c.maxX; x++) {
            const idx = row + x;
            if (labels[idx] === c.label) {
              data[idx * 4 + 3] = 0; // Clear alpha to transparent
              if (mask) mask[idx] = 255; // Mark as background in mask
              purgedPixels++;
            }
          }
        }
      }
    }

    // 3. Compute clean bounding box strictly from remaining unmasked subject
    let minX = width, maxX = -1, minY = height, maxY = -1;
    for (let y = 0; y < height; y++) {
      const row = y * width;
      for (let x = 0; x < width; x++) {
        const idx = row + x;
        if ((!mask || mask[idx] === 0) && data[idx * 4 + 3] > minAlpha) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    const boundingBox = maxX >= minX && maxY >= minY ? { minX, maxX, minY, maxY } : null;

    return {
      purgedPixels,
      purgedComponents,
      boundingBox
    };
  }
}

